import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import t from 'translation.json';

export type Lang = 'fr' | 'en';

export interface LanguageContextType {
  lang: Lang;
  res: typeof t['fr'] | typeof t['en'];
  onLanguageChange: (language: Lang) => void;
}

export type LangStringMap = {
  [key in Lang]: string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
LanguageContext.displayName = "LanguageContext";

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storedLang = localStorage?.getItem("lang") as Lang;
  const browserLang = (navigator.language.slice(0, 2) as Lang) || 'fr';

  const initialLang: Lang = storedLang === 'fr' || storedLang === 'en' ? storedLang : browserLang;

  const [lang, setLang] = useState<Lang>(initialLang);

  useEffect(() => {
    if (!storedLang) {
      localStorage.setItem('lang', initialLang);
    }
  }, [initialLang, storedLang]);

  const onLanguageChange = useCallback(
    (newLang: Lang) => {
      localStorage.setItem('lang', newLang);
      setLang(newLang);
    },
    []
  );

  const res = t[lang];

  return (
    <LanguageContext.Provider value={{ lang, res, onLanguageChange }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
